import { SEMINAR_INFORMATION_TEXT_MAX_LENGTH, SEMINAR_INFORMATION_ADJUST_SCHEDULE_FIXED, SEMINAR_INFORMATION_APPLICATION_PROCESS_URL, SEMINAR_INFORMATION_APPLICATION_PROCESS_EMAIL, SEMINAR_INFORMATION_STATUS_DRAFT, SEMINAR_INFORMATION_DATA_TYPE_PREVIEW, } from '@/define/consts/seminar/seminar_information';
import { ACCEPT_IMAGE_MIME_TYPE, isAllowAcceptTypeFiles, } from '@/utils/validate/conditions';
import { functionalValidate, isAllowMegaSizeFile, isFileValid, isHasArray, isNullableAllowedEmailDomain, isNullableEmail, isRequiredText, isUrl, strMax, } from '../utils';
/**
 * 下書き（プレビュー）以外の場合にバリデーションを適用する
 * @param validate
 * @returns {function(*=, *=, *, *): (*|string)}
 */
const validateIfNotDraft = (validate) => (value, record, props) => {
    if (record.status === SEMINAR_INFORMATION_STATUS_DRAFT.value ||
        record.dataType === SEMINAR_INFORMATION_DATA_TYPE_PREVIEW.value) {
        return '';
    }
    return validate(value, record, props);
};
/**
 * 公開状態が下書きでなく、対象キーがパラメータの値と一致する場合に引数のバリデーションを適用する
 * @param validate
 * @param key
 * @param define
 * @returns {(function(*=, *=, *=): (string|*|string))|*}
 */
const validateIfValueIs = (validate, key, define) => (value, record, props) => {
    if (record.status === SEMINAR_INFORMATION_STATUS_DRAFT.value ||
        record.dataType === SEMINAR_INFORMATION_DATA_TYPE_PREVIEW.value) {
        return '';
    }
    return record[key] === define ? validate(value, record, props) : '';
};
/**
 * 開催日程の「日程を設定する」が選択されてる場合にバリデーションを適用する
 * @param validate
 * @returns {(function(*=, *=, *=): (string|*|string))|*}
 */
const validateIfAdjustScheduleFixed = (validate) => validateIfValueIs(validate, 'adjustSchedule', SEMINAR_INFORMATION_ADJUST_SCHEDULE_FIXED.value);
/**
 * セミナー申込方法で自社URLが選択されてる場合にバリデーションを適用する
 * @param validate
 * @returns {(function(*=, *=, *=): (string|*|string))|*}
 */
const validateIfApplicationProcessUrl = (validate) => validateIfValueIs(validate, 'applicationProcess', SEMINAR_INFORMATION_APPLICATION_PROCESS_URL.value);
/**
 * セミナー申込方法でメールが選択されてる場合にバリデーションを適用する
 * @param validate
 * @returns {(function(*=, *=, *=): (string|*|string))|*}
 */
const validateIfApplicationProcessEmail = (validate) => validateIfValueIs(validate, 'applicationProcess', SEMINAR_INFORMATION_APPLICATION_PROCESS_EMAIL.value);
export const professionalSeminarValidate = {
    adjustSchedule: functionalValidate([
        validateIfNotDraft(isRequiredText('開催日時を選択してください')),
    ]),
    applicationDeadline: functionalValidate([
        validateIfAdjustScheduleFixed(isRequiredText('募集締切日を入力してください')),
    ]),
    applicationProcess: functionalValidate([
        validateIfNotDraft(isRequiredText('セミナー申込方法を選択してください')),
    ]),
    applicationUrl: functionalValidate([
        validateIfApplicationProcessUrl(isRequiredText('自社のセミナーページのURLを入力してください')),
        validateIfApplicationProcessUrl(isUrl('正しいURLを入力してください')),
    ]),
    categories: functionalValidate([
        validateIfNotDraft(isHasArray('カテゴリーを選択してください')),
    ]),
    dates: {
        endDate: functionalValidate([
            validateIfAdjustScheduleFixed(isRequiredText('終了日時を入力してください')),
        ]),
        startDate: functionalValidate([
            validateIfAdjustScheduleFixed(isRequiredText('開始日時を入力してください')),
        ]),
    },
    description: functionalValidate([
        validateIfNotDraft(isRequiredText('セミナー概要を入力してください')),
        validateIfNotDraft(strMax(SEMINAR_INFORMATION_TEXT_MAX_LENGTH, `セミナー概要は${SEMINAR_INFORMATION_TEXT_MAX_LENGTH}文字以内で入力してください`)),
    ]),
    documentFile: functionalValidate([
        (value) => {
            if (value instanceof File) {
                return isFileValid()(value);
            }
            return '';
        },
        (value) => {
            if (value instanceof File) {
                return isAllowAcceptTypeFiles('application/pdf', 'PDFファイルを選択してください')(value);
            }
            return '';
        },
    ]),
    handlePersonalInformation: functionalValidate([
        validateIfApplicationProcessEmail(isRequiredText('個人情報の取り扱い方法を選択してください')),
    ]),
    liaisons: {
        array: functionalValidate([
            validateIfApplicationProcessEmail(isHasArray('お問い合わせ窓口を少なくとも1つ以上入力してください')),
        ]),
        email: functionalValidate([
            validateIfApplicationProcessEmail(isRequiredText('メールアドレスを入力してください')),
            validateIfNotDraft(isNullableEmail('メールアドレス形式が正しくありません')),
            validateIfNotDraft(isNullableAllowedEmailDomain('フリーメールアドレスは使用できません')),
        ]),
        firstName: functionalValidate([
            validateIfApplicationProcessEmail(isRequiredText('名を入力してください')),
            validateIfNotDraft(strMax(20, '名は20文字以内で入力してください')),
        ]),
        lastName: functionalValidate([
            validateIfApplicationProcessEmail(isRequiredText('姓を入力してください')),
            validateIfNotDraft(strMax(20, '姓は20文字以内で入力してください')),
        ]),
    },
    metaDescription: functionalValidate([
        validateIfNotDraft(strMax(2000, 'メタタグは2000文字以内で入力してください')),
    ]),
    participationFee: functionalValidate([
        validateIfNotDraft(isRequiredText('参加費用を選択してください')),
    ]),
    participationTypes: functionalValidate([
        validateIfNotDraft(isHasArray('参加形態を選択してください')),
    ]),
    privacyPolicyUrl: functionalValidate([
        validateIfApplicationProcessEmail(isRequiredText('プライバシーポリシーURLを入力してください')),
        validateIfApplicationProcessEmail(isUrl('正しいURLを入力してください')),
    ]),
    solveIssue: functionalValidate([
        validateIfNotDraft(isRequiredText('こんな人におすすめを入力してください')),
        validateIfNotDraft(strMax(SEMINAR_INFORMATION_TEXT_MAX_LENGTH, `こんな人におすすめは${SEMINAR_INFORMATION_TEXT_MAX_LENGTH}文字以内で入力してください`)),
    ]),
    status: functionalValidate([isRequiredText('公開状態を選択してください')]),
    thumbnailPicture: functionalValidate([
        (value) => {
            if (value instanceof File) {
                return isAllowAcceptTypeFiles(ACCEPT_IMAGE_MIME_TYPE, '画像ファイルを選択してください')(value);
            }
            return '';
        },
        (value) => {
            if (value instanceof File) {
                return isAllowMegaSizeFile(10, '10MB以下のファイルを選択してください')(value);
            }
            return '';
        },
    ]),
    title: functionalValidate([
        validateIfNotDraft(isRequiredText('タイトルを入力してください')),
        validateIfNotDraft(strMax(SEMINAR_INFORMATION_TEXT_MAX_LENGTH, `タイトルは${SEMINAR_INFORMATION_TEXT_MAX_LENGTH}文字以内で入力してください`)),
    ]),
    type: functionalValidate([
        validateIfNotDraft(isRequiredText('タイプを選択してください')),
    ]),
};
export default professionalSeminarValidate;
